import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import {
  Lock, Armchair, Zap, Droplets, Thermometer, ChefHat, Package, CupSoda, Trash2,
  Boxes, AlertTriangle, Utensils, Clock, CheckCircle, Loader2, ChevronDown,
  ExternalLink, Flame, RefreshCw, History, Sparkles, Info,
} from 'lucide-react';

const ICONS = {
  Lock, Armchair, Zap, Droplets, Thermometer, ChefHat, Package, CupSoda, Trash2,
  Boxes, AlertTriangle, Utensils, Clock, CheckCircle,
};

export function OuverturePage() {
  return <GenericChecklistPage slug="ouverture" title="Procedure d'ouverture — Chana Thai" subtitle="Hygiene (HACCP) • Securite • Qualite produit • Efficacite" iconColor="text-orange-600" />;
}

export function FinMidiPage() {
  return <GenericChecklistPage slug="fin-midi" title="Fin de service midi — Chana Thai" subtitle="Continuite service soir • Hygiene • Tracabilite • Mise en pause" iconColor="text-amber-600" />;
}

export function FermetureSoirPage() {
  return <GenericChecklistPage slug="fermeture-soir" title="Fin de service soir / Fermeture — Chana Thai" subtitle="Fermeture securisee • Hygiene • Tracabilite • Preparation lendemain" iconColor="text-indigo-600" />;
}

function GenericChecklistPage({ slug, title, subtitle, iconColor }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  // Utilise les routes generiques /checklists/{slug}/*
  const base = `/checklists/${slug}`;

  const fetchChecklist = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${base}/today`);
      setChecklist(res.data);
    } catch { toast.error('Erreur chargement checklist'); }
    finally { setLoading(false); }
  }, [api, base]);

  const fetchHistory = useCallback(async () => {
    try { const res = await api.get(`${base}/history`); setHistory(res.data); }
    catch { /* ignore */ }
  }, [api, base]);

  useEffect(() => { fetchChecklist(); fetchHistory(); }, [fetchChecklist, fetchHistory]);

  const toggleItem = async (sectionId, itemId, check) => {
    setChecklist(c => ({
      ...c,
      sections: c.sections.map(s => s.id !== sectionId ? s : {
        ...s,
        items: s.items.map(it => it.id !== itemId ? it : { ...it, check })
      })
    }));
    try {
      const res = await api.put(`${base}/today/item`, { section_id: sectionId, item_id: itemId, check });
      setChecklist(res.data);
    } catch { toast.error('Erreur'); fetchChecklist(); }
  };

  const updateNote = async (sectionId, itemId, note) => {
    try {
      const res = await api.put(`${base}/today/item`, { section_id: sectionId, item_id: itemId, note });
      setChecklist(res.data);
    } catch { toast.error('Erreur'); }
  };

  const complete = async () => {
    try {
      const res = await api.post(`${base}/today/complete`);
      setChecklist(res.data);
      toast.success(`Checklist terminee ! ${res.data.score_check}/${res.data.score_total} (${res.data.score_pct}%)`);
      fetchHistory();
    } catch { toast.error('Erreur'); }
  };

  if (loading || !checklist) return <div className="flex h-96 items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  const total = checklist.sections.reduce((acc, s) => acc + s.items.length, 0);
  const checked = checklist.sections.reduce((acc, s) => acc + s.items.filter(it => it.check).length, 0);
  const pct = total > 0 ? Math.round((checked / total) * 100) : 0;
  const critiquesNonOk = checklist.sections.filter(s => s.critique).flatMap(s => s.items.filter(it => !it.check).map(it => ({ s: s.titre, it: it.label })));
  const terminee = checklist.statut === 'terminee';

  return (
    <div className="space-y-4 pb-8" data-testid="ouverture-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className={`w-6 h-6 ${iconColor || 'text-orange-600'}`} />
            {title}
          </h1>
          <p className="text-muted-foreground text-sm">{subtitle}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => { fetchChecklist(); fetchHistory(); }} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Rafraichir
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowHistory(v => !v)} data-testid="toggle-history">
            <History className="w-4 h-4 mr-1" /> Historique ({history.length})
          </Button>
        </div>
      </div>

      {/* Progress */}
      <Card className={terminee ? 'border-2 border-green-400 bg-green-50/40' : pct === 100 ? 'border-green-300 bg-green-50/30' : pct > 0 ? 'border-orange-300' : ''}>
        <CardContent className="py-3">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs text-muted-foreground">{checklist.date} • {terminee ? 'Terminee' : 'En cours'}</p>
              <p className="text-2xl font-bold tabular-nums">{checked}<span className="text-base text-muted-foreground">/{total}</span> <span className="text-sm font-normal">({pct}%)</span></p>
            </div>
            {!terminee && pct === 100 && (
              <Button onClick={complete} className="bg-green-600 hover:bg-green-700" data-testid="complete-btn">
                <CheckCircle className="w-4 h-4 mr-1" /> Cloturer l'ouverture
              </Button>
            )}
            {terminee && (
              <Badge className="bg-green-600 text-white text-base px-3 py-1">
                <CheckCircle className="w-4 h-4 mr-1" /> Cloturee {checklist.completed_at?.slice(11, 16)}
              </Badge>
            )}
          </div>
          <Progress value={pct} className="h-2" />
        </CardContent>
      </Card>

      {/* Alerte critiques non OK */}
      {critiquesNonOk.length > 0 && !terminee && (
        <Card className="border-2 border-red-300 bg-red-50/40" data-testid="critical-alert">
          <CardContent className="py-3 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-red-700 mb-1">Points critiques a ne jamais negliger ({critiquesNonOk.length} restants)</p>
              <ul className="text-xs text-red-700/80 space-y-0.5">
                {critiquesNonOk.slice(0, 5).map((c, i) => <li key={i}>• <b>{c.s}</b> : {c.it}</li>)}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sections */}
      <div className="space-y-2">
        {checklist.sections.map((s, idx) => (
          <SectionCard
            key={s.id}
            section={s}
            idx={idx}
            onToggle={toggleItem}
            onNote={updateNote}
            terminee={terminee}
            navigate={navigate}
          />
        ))}
      </div>

      {/* Info rules permanent */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Flame className="w-4 h-4 text-red-600" /> Points critiques (a ne jamais negliger)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
          <Badge variant="outline" className="justify-start py-1.5 px-2"><Thermometer className="w-3 h-3 mr-1 text-red-600" /> Temperature produits</Badge>
          <Badge variant="outline" className="justify-start py-1.5 px-2"><AlertTriangle className="w-3 h-3 mr-1 text-red-600" /> DLC</Badge>
          <Badge variant="outline" className="justify-start py-1.5 px-2"><Droplets className="w-3 h-3 mr-1 text-blue-600" /> Lavage mains</Badge>
          <Badge variant="outline" className="justify-start py-1.5 px-2"><Package className="w-3 h-3 mr-1 text-purple-600" /> Tracabilite</Badge>
          <Badge variant="outline" className="justify-start py-1.5 px-2"><Sparkles className="w-3 h-3 mr-1 text-green-600" /> Proprete</Badge>
        </CardContent>
      </Card>

      {/* History */}
      {showHistory && (
        <Card data-testid="history-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> 60 derniers jours</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {history.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">Aucun historique</p> :
              history.map(h => (
                <div key={h.id} className="flex items-center justify-between p-2 rounded border text-sm">
                  <span className="font-medium">{h.date}</span>
                  <div className="flex items-center gap-2">
                    {h.statut === 'terminee' ? (
                      <Badge className={`${(h.score_pct ?? 0) >= 90 ? 'bg-green-100 text-green-700' : (h.score_pct ?? 0) >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {h.score_check}/{h.score_total} ({h.score_pct}%)
                      </Badge>
                    ) : (
                      <Badge variant="outline">En cours</Badge>
                    )}
                    {h.critiques_non_ok?.length > 0 && (
                      <Badge className="bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3 mr-1" /> {h.critiques_non_ok.length} NC</Badge>
                    )}
                  </div>
                </div>
              ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SectionCard({ section, idx, onToggle, onNote, terminee, navigate }) {
  const Icon = ICONS[section.icone] || Package;
  const [open, setOpen] = useState(true);
  const [localNote, setLocalNote] = useState({});

  const checked = section.items.filter(it => it.check).length;
  const total = section.items.length;
  const allDone = checked === total && total > 0;

  return (
    <Card className={`${section.critique ? 'border-red-200' : ''} ${allDone ? 'bg-green-50/30' : ''}`} data-testid={`section-${section.id}`}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-2 cursor-pointer hover:bg-muted/30 transition">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${allDone ? 'bg-green-600 text-white' : section.critique ? 'bg-red-100 text-red-700' : 'bg-muted'}`}>
                  {allDone ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <div className="min-w-0">
                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                    <span>{idx + 1}. {section.titre}</span>
                    {section.critique && <Badge className="bg-red-100 text-red-700 text-[10px]">CRITIQUE</Badge>}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">{checked}/{total} items</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {section.lien_module && (
                  <Button variant="outline" size="sm" className="h-7" onClick={(e) => { e.stopPropagation(); navigate(section.lien_module); }}>
                    <ExternalLink className="w-3 h-3 mr-1" /> {section.lien_label}
                  </Button>
                )}
                <ChevronDown className={`w-4 h-4 transition ${open ? 'rotate-180' : ''}`} />
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-2">
            {section.items.map(it => (
              <div key={it.id} className={`rounded-md border p-2 ${it.check ? 'bg-green-50/60 border-green-200' : 'bg-background'}`}>
                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={it.check}
                    disabled={terminee}
                    onCheckedChange={v => onToggle(section.id, it.id, !!v)}
                    className="mt-0.5"
                    data-testid={`check-${it.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${it.check ? 'line-through text-muted-foreground' : ''}`}>
                      {it.label}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {it.heure && <span className="text-[10px] text-muted-foreground">{it.heure}</span>}
                      {it.utilisateur && <span className="text-[10px] text-muted-foreground truncate">{it.utilisateur}</span>}
                      {it.lien && (
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => navigate(it.lien)}>
                          <ExternalLink className="w-3 h-3 mr-0.5" /> Ouvrir
                        </Button>
                      )}
                      {it.requis_temp_produit && (
                        <Badge variant="outline" className="text-[9px] border-orange-300 text-orange-700">
                          <Thermometer className="w-2.5 h-2.5 mr-0.5" /> Enregistrer dans HACCP
                        </Badge>
                      )}
                    </div>
                    {it.type === 'textarea' && (
                      <Input
                        value={localNote[it.id] ?? it.note ?? ''}
                        onChange={e => setLocalNote(n => ({...n, [it.id]: e.target.value}))}
                        onBlur={() => onNote(section.id, it.id, localNote[it.id] ?? '')}
                        placeholder={it.placeholder || 'Note...'}
                        disabled={terminee}
                        className="mt-1 h-8 text-xs"
                        data-testid={`note-${it.id}`}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
